import { useEffect, useState, useRef, Suspense, lazy } from 'react';
import Layout from './layouts/Layout';
import NewRiskManagementLayout from './layouts/NewRiskManagementLayout';
import NewComplianceManagementLayout from './layouts/NewComplianceManagementLayout';
import OldStrategicPerformanceLayout from './layouts/OldStrategicPerformanceLayout';
import StrategicPerformanceLayout from './layouts/StrategicPerformanceLayout';
import InternalAuditModuleLayout from './layouts/InternalAuditModuleLayout';
import './styles/globals.css';
import {
  GridRegular,
  Alert24Regular,
  LearningApp24Regular,
  DocumentText24Regular,
  ClipboardCheckmark24Regular,
  DataTrending24Regular,
  Handshake24Regular,
  PeopleTeam24Regular,
  Globe24Regular,
  Home24Regular,
  Settings24Regular,
  ShieldCheckmark24Regular,
  ShieldAdd24Regular,
  PersonFeedback24Regular,
  ShieldTask24Regular,
  Clock24Regular
} from '@fluentui/react-icons';
import { useAuth } from './contexts/AuthContext';
import { fetchNotifications } from './store/slices/notificationSlice';
import { useAppDispatch } from './hooks/useAppDispatch';
import { fetchAnnualTargets } from './store/slices/scorecardSlice';
import { useSocket } from './hooks/useSocket';
import { SocketEvent } from './types/socket';
import { fetchTeams } from './store/slices/teamsSlice';
import { api } from './services/api';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  CircularProgress,
  Box
} from '@mui/material';
import NoModuleEnabled from './pages/NoModuleEnabled';
import NoPermission from './pages/NoPermission';
import SupportChatWidget from './components/SupportChatWidget';
import SupportDashboard from './pages/SupportDashboard';

// Lazy load all main page components
const AnnualCorporateScorecard = lazy(() => import('./pages/scorecards'));
const ManagePage = lazy(() => import('./pages/manage').then(m => ({ default: m.ManagePage })));
const OrganizationPerformance = lazy(() => import('./pages/organization_performance'));
const NotificationPage = lazy(() => import('./pages/notification'));
const MyPerformanceAgreement = lazy(() => import('./pages/my_performance_agreement'));
const MyPerformanceAssessment = lazy(() => import('./pages/my_performance_assessment'));
const Reports = lazy(() => import('./pages/reports'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const EmployeeDevPlan = lazy(() => import('./pages/employee_dev_plan'));
const TeamsPage = lazy(() => import('./pages/teams'));
const Feedback = lazy(() => import('./pages/feedback'));
const PerformanceCalibration = lazy(() => import('./pages/performance_calibration'));
// const ComplianceManagement = lazy(() => import('./pages/compliance_management'));
const NewComplianceManagement = lazy(() => import('./pages/new_compliance_management'));
const RiskManagement = lazy(() => import('./pages/risk_management'));
const NewRiskManagement = lazy(() => import('./pages/new_risk_management/index'));
const OldStrategicPerformance = lazy(() => import('./pages/old_strategic_performance/index'));
const StrategicPerformance = lazy(() => import('./pages/strategic_performance/index'));
const InternalAuditModule = lazy(() => import('./pages/internal_audit_module'));
const AuditTrail = lazy(() => import('./pages/audit_trail'));

const iconSize = 24;

function Main() {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [isPerformanceManagementModuleEnabled, setIsPerformanceManagementModuleEnabled] = useState(false);
  const [isFeedbackModuleEnabled, setIsFeedbackModuleEnabled] = useState(false);
  const [isPerformanceCalibrationModuleEnabled, setIsPerformanceCalibrationModuleEnabled] = useState(false);
  const [isPerformanceManagementQuantitativeModuleEnabled, setIsPerformanceManagementQuantitativeModuleEnabled] = useState(false);
  const [isPerformanceManagementCascadeModuleEnabled, setIsPerformanceManagementCascadeModuleEnabled] = useState(false);
  const [isEmployeeCompetencyModuleEnabled, setIsEmployeeCompetencyModuleEnabled] = useState(false);
  const [isComplianceModuleEnabled, setIsComplianceModuleEnabled] = useState(false);
  const [isRiskModuleEnabled, setIsRiskModuleEnabled] = useState(false);
  const [isNewRiskManagementModuleEnabled, setIsNewRiskManagementModuleEnabled] = useState(false);
  const [isOldStrategicPerformanceModuleEnabled, setIsOldStrategicPerformanceModuleEnabled] = useState(false);
  const [isStrategicPerformanceModuleEnabled, setIsStrategicPerformanceModuleEnabled] = useState(false);
  const [isInternalAuditModuleEnabled, setIsInternalAuditModuleEnabled] = useState(false);

  // Add socket subscription
  const { subscribe, unsubscribe } = useSocket(SocketEvent.NOTIFICATION, (data) => {
    dispatch(fetchNotifications());
  });

  // Track which tenant we've checked modules for to prevent re-checking
  const checkedTenantIdRef = useRef<string | null>(null);
  const [modulesChecked, setModulesChecked] = useState(false);
  const checkingModulesRef = useRef(false);

  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(fetchAnnualTargets());
    dispatch(fetchTeams(user?.tenantId));
    // Subscribe to notification events
    subscribe(SocketEvent.NOTIFICATION, (data) => {
      dispatch(fetchNotifications());
    });

    // Only check modules once per tenant to prevent infinite loops
    if (!user?.tenantId) {
      return;
    }
    
    // If tenant changed, reset the checked flag
    if (checkedTenantIdRef.current !== user.tenantId) {
      checkedTenantIdRef.current = user.tenantId;
      setModulesChecked(false);
      checkingModulesRef.current = false;
      // Continue to check modules for new tenant
    } else if (modulesChecked || checkingModulesRef.current) {
      // Already checked for this tenant or currently checking, skip
      return;
    }
    
    // Mark as checking to prevent concurrent checks
    checkingModulesRef.current = true;

    //check if feedback module is enabled for company
    const checkPerformanceManagementModule = async () => {
      const isModuleEnabled = await api.get('/module/PerformanceManagement/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsPerformanceManagementModuleEnabled(true);
      }
    }

    //check if feedback module is enabled for company
    const checkFeedbackModule = async () => {
      const isModuleEnabled = await api.get('/module/Feedback/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsFeedbackModuleEnabled(true);
      }
    }

    const checkPerformanceCalibrationModule = async () => {
      const isModuleEnabled = await api.get('/module/PerformanceCalibration/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsPerformanceCalibrationModuleEnabled(true);
      }
    }

    //check if feedback module is enabled for company
    const checkPerformanceManagementQuantitativeModule = async () => {
      const isModuleEnabled = await api.get('/module/PerformanceManagementQuantitative/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsPerformanceManagementQuantitativeModuleEnabled(true);
      }
    }

    const checkPerformanceManagementCascadeModule = async () => {
      const isModuleEnabled = await api.get('/module/PerformanceManagementCascade/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsPerformanceManagementCascadeModuleEnabled(true);
      }
    }

    const checkEmployeeCompetencyModule = async () => {
      const isModuleEnabled = await api.get('/module/EmployeeCompetency/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsEmployeeCompetencyModuleEnabled(true);
      }
    }

    const checkComplianceModule = async () => {
      const isModuleEnabled = await api.get('/module/Compliance/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsComplianceModuleEnabled(true);
      }
    }

    const checkRiskModule = async () => {
      const isModuleEnabled = await api.get('/module/Risk/is-enabled');
      if (isModuleEnabled.data.data.isEnabled) {
        setIsRiskModuleEnabled(true);
      }
    }

    const checkNewRiskManagementModule = async () => {
      try {
        const isModuleEnabled = await api.get('/module/NewRiskManagement/is-enabled');
        console.log('New Risk Management Module Status:', isModuleEnabled.data);
        if (isModuleEnabled.data.data.isEnabled) {
          console.log('Setting New Risk Management module as enabled');
          setIsNewRiskManagementModuleEnabled(true);
        } else {
          console.log('New Risk Management module is disabled');
          setIsNewRiskManagementModuleEnabled(false);
        }
      } catch (error) {
        console.error('Error checking New Risk Management module:', error);
        setIsNewRiskManagementModuleEnabled(false);
      }
    }
    checkNewRiskManagementModule();

    const checkOldStrategicPerformanceModule = async () => {
      try {
        const isModuleEnabled = await api.get('/module/OldStrategicPerformance/is-enabled');
        console.log('Old Strategic Performance Module Status:', isModuleEnabled.data);
        if (isModuleEnabled.data.data.isEnabled) {
          console.log('Setting Old Strategic Performance module as enabled');
          setIsOldStrategicPerformanceModuleEnabled(true);
        } else {
          console.log('Old Strategic Performance module is disabled');
          setIsOldStrategicPerformanceModuleEnabled(false);
        }
      } catch (error) {
        console.error('Error checking Old Strategic Performance module:', error);
        setIsOldStrategicPerformanceModuleEnabled(false);
      }
    }
    checkOldStrategicPerformanceModule();

    const checkStrategicPerformanceModule = async () => {
      try {
        const isModuleEnabled = await api.get('/module/StrategicPerformance/is-enabled');
        console.log('Strategic Performance Module Status:', isModuleEnabled.data);
        if (isModuleEnabled.data.data.isEnabled) {
          console.log('Setting Strategic Performance module as enabled');
          setIsStrategicPerformanceModuleEnabled(true);
        } else {
          console.log('Strategic Performance module is disabled');
          setIsStrategicPerformanceModuleEnabled(false);
        }
      } catch (error) {
        console.error('Error checking Strategic Performance module:', error);
        setIsStrategicPerformanceModuleEnabled(false);
      }
    }

    const checkInternalAuditModule = async () => {
      try {
        const isModuleEnabled = await api.get('/module/InternalAuditModule/is-enabled');
        console.log('Internal Audit Module API Response:', isModuleEnabled.data);
        // Response interceptor wraps responses, so we access via data.data
        if (isModuleEnabled.data.data?.isEnabled) {
          console.log('Setting Internal Audit module as enabled');
          setIsInternalAuditModuleEnabled(true);
        } else {
          console.log('Internal Audit module is disabled');
          setIsInternalAuditModuleEnabled(false);
        }
      } catch (error) {
        console.error('Error checking Internal Audit module:', error);
        setIsInternalAuditModuleEnabled(false);
      }
    }

    const checkAllModules = async () => {
      try {
        await Promise.all([
          checkPerformanceManagementModule(),
          checkFeedbackModule(),
          checkPerformanceCalibrationModule(),
          checkPerformanceManagementQuantitativeModule(),
          checkPerformanceManagementCascadeModule(),
          checkComplianceModule(),
          checkRiskModule(),
          checkNewRiskManagementModule(),
          checkStrategicPerformanceModule(),
          checkInternalAuditModule(),
          checkEmployeeCompetencyModule()
        ]);
        setModulesChecked(true);
      } finally {
        checkingModulesRef.current = false;
      }
    };
    checkAllModules();

    // Cleanup subscription
    return () => {
      unsubscribe(SocketEvent.NOTIFICATION);
    };
  }, [dispatch, subscribe, unsubscribe, user?.tenantId]);

  const isSuperUser = user?.role === 'SuperUser';
  const isAppOwner = user?.email === process.env.REACT_APP_OWNER_EMAIL;
  const [isDevMember, setIsDevMember] = useState(false);
  const [isPerformanceCalibrationMember, setIsPerformanceCalibrationMember] = useState(false);
  const [isTeamOwner, setIsTeamOwner] = useState(false);
  const [hasSupervisees, setHasSupervisees] = useState(false);
  const [isComplianceSuperUser, setIsComplianceSuperUser] = useState(false);
  const [isComplianceChampion, setIsComplianceChampion] = useState(false);
  const [isRiskSuperUser, setIsRiskSuperUser] = useState(false);
  const [isRiskChampion, setIsRiskChampion] = useState(false);
  const [isNewRiskManagementSuperUser, setIsNewRiskManagementSuperUser] = useState(false);
  // OLD Strategic Performance Module - uses isStrategicPerformanceManagementSuperUser
  const [isOldStrategicPerformanceSuperUser, setIsOldStrategicPerformanceSuperUser] = useState(false);
  // NEW Strategic Performance Module - uses isStrategicPerformanceSuperUser
  const [isStrategicPerformanceSuperUser, setIsStrategicPerformanceSuperUser] = useState(false);
  const [isStrategicPerformanceEmployee, setIsStrategicPerformanceEmployee] = useState(false);
  const [isInternalAuditModuleSuperUser, setIsInternalAuditModuleSuperUser] = useState(false);
  const [isInternalAuditUser, setIsInternalAuditUser] = useState(false);
  const [isDepartmentUser, setIsDepartmentUser] = useState(false);
  useEffect(() => {
    if (user) {
      console.log('User:', user);
      setIsComplianceSuperUser(!!user?.isComplianceSuperUser);
      setIsComplianceChampion(!!user?.isComplianceChampion);
      setIsTeamOwner(!!user?.isTeamOwner);
      setIsDevMember(!!user?.isDevMember);
      setIsPerformanceCalibrationMember(!!user?.isPerformanceCalibrationMember);
      setIsRiskSuperUser(!!user?.isRiskSuperUser);
      setIsRiskChampion(!!user?.isRiskChampion);
      setIsNewRiskManagementSuperUser(!!user?.isNewRiskManagementSuperUser);
      // OLD Strategic Performance Module - separate flag
      setIsOldStrategicPerformanceSuperUser(!!(user as any)?.isStrategicPerformanceManagementSuperUser);
      // NEW Strategic Performance Module - separate flag
      setIsStrategicPerformanceSuperUser(!!user?.isStrategicPerformanceSuperUser);
      setIsInternalAuditModuleSuperUser(!!user?.isInternalAuditModuleSuperUser);
      setIsInternalAuditUser(!!user?.isInternalAuditUser);
      setIsDepartmentUser(!!user?.isDepartmentUser);
      console.log('Internal Audit Module Super User flag from user:', !!user?.isInternalAuditModuleSuperUser);
      console.log('Internal Audit User flag from user:', !!user?.isInternalAuditUser);
    }
  }, [user, user?.tenantId]);

  // Check if user has supervisees (users who selected them as supervisor)
  useEffect(() => {
    const checkHasSupervisees = async () => {
      console.log('[DEBUG hasSupervisees] Starting check, user object:', user);
      console.log('[DEBUG hasSupervisees] user._id:', user?._id);
      console.log('[DEBUG hasSupervisees] user.id:', user?.id);
      
      if (!user?._id && !user?.id) {
        console.log('[DEBUG hasSupervisees] No user ID found, setting to false');
        setHasSupervisees(false);
        return;
      }

      try {
        // Collect all IDs this user may be stored as: Microsoft ID (GUID) and MongoDB _id
        const currentUserIds = new Set<string>();
        if (user?.id) currentUserIds.add(user.id.toString());
        if (user?._id) currentUserIds.add(user._id.toString());

        // Also fetch the DB _id in case the user object only has the Microsoft ID
        if (!user?._id && user?.id) {
          try {
            const userResponse = await api.get('/auth/verify');
            const dbId = userResponse.data?.data?.user?._id?.toString();
            if (dbId) currentUserIds.add(dbId);
          } catch (err) {
            console.error('[DEBUG hasSupervisees] Error getting user _id:', err);
          }
        }

        if (currentUserIds.size === 0) {
          console.log('[DEBUG hasSupervisees] Could not determine any user ID, setting to false');
          setHasSupervisees(false);
          return;
        }

        console.log('[DEBUG hasSupervisees] Looking for supervisorId matching any of:', [...currentUserIds]);

        // Get all annual targets first, then check each one
        const annualTargetsResponse = await api.get('/score-card/annual-targets');
        const annualTargets = annualTargetsResponse.data?.data || [];
        console.log('[DEBUG hasSupervisees] Found annual targets:', annualTargets.length);
        
        let hasAnySupervisees = false;
        let foundMatches = 0;
        
        // Check each annual target for supervisees
        for (const target of annualTargets) {
          try {
            const response = await api.get('/personal-performance/all-performances', {
              params: { annualTargetId: target._id }
            });
            
            // Backend returns direct array, not wrapped in { data: [...] }
            const performances = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            
            if (response.status === 200 && performances.length > 0) {
              console.log(`[DEBUG hasSupervisees] Annual target ${target._id}: ${performances.length} performances`);
              
              const foundInThisTarget = performances.some((perf: any) => {
                if (!perf.quarterlyTargets || perf.quarterlyTargets.length === 0) {
                  return false;
                }
                
                return perf.quarterlyTargets.some((qt: any) => {
                  const supervisorId = qt.supervisorId?.toString() || qt.supervisorId;
                  const actingSupervisorId = qt.actingSupervisorId?.toString() || qt.actingSupervisorId;
                  
                  const matches = currentUserIds.has(supervisorId) || currentUserIds.has(actingSupervisorId);
                  if (matches) {
                    foundMatches++;
                    console.log('[DEBUG hasSupervisees] MATCH FOUND!', {
                      annualTargetId: target._id,
                      annualTargetName: target.name,
                      performanceUserId: perf.userId?._id || perf.userId,
                      performanceUserName: perf.userId?.name || perf.fullName,
                      supervisorId,
                      actingSupervisorId,
                      currentUserIds: [...currentUserIds],
                      quarter: qt.quarter
                    });
                  }
                  return matches;
                });
              });
              
              if (foundInThisTarget) {
                hasAnySupervisees = true;
                // No need to check other targets if we found one
                break;
              }
            }
          } catch (err) {
            console.error(`[DEBUG hasSupervisees] Error checking annual target ${target._id}:`, err);
          }
        }

        console.log('[DEBUG hasSupervisees] Total matches found:', foundMatches);
        console.log('[DEBUG hasSupervisees] Final result:', hasAnySupervisees);
        setHasSupervisees(hasAnySupervisees);
      } catch (error: any) {
        console.error('[DEBUG hasSupervisees] Error checking for supervisees:', error);
        console.error('[DEBUG hasSupervisees] Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setHasSupervisees(false);
      }
    };

    if (user) {
      checkHasSupervisees();
    } else {
      console.log('[DEBUG hasSupervisees] No user object, setting to false');
      setHasSupervisees(false);
    }
  }, [user?._id, user?.id]);

  // Check if current user is a strategic performance employee (NEW MODULE ONLY)
  useEffect(() => {
    const checkStrategicPerformanceEmployee = async () => {
      if (!user?.id || !isStrategicPerformanceModuleEnabled) {
        setIsStrategicPerformanceEmployee(false);
        return;
      }

      try {
        const response = await api.get('/strategic-performance/settings/employees');
        const employees = response.data?.data || response.data || [];
        
        const isEmployee = employees.some((emp: any) => {
          const userId = typeof emp.userId === 'string' 
            ? emp.userId 
            : (emp.userId?._id || emp.userId?.id || emp.userId);
          const userMatch = userId === user._id || userId === user.id;
          return userMatch && emp.status === 'active';
        });
        
        setIsStrategicPerformanceEmployee(isEmployee);
      } catch (error) {
        console.error('Error checking strategic performance employee status:', error);
        setIsStrategicPerformanceEmployee(false);
      }
    };

    checkStrategicPerformanceEmployee();
  }, [user?.id, user?._id, isStrategicPerformanceModuleEnabled]);

  const pages = [
    {
      path: "/dashboard/*",
      element: Dashboard,
      title: "Dashboard",
      icon: <Home24Regular fontSize={iconSize} />,
      tabs: ['Dashboard'],
      show: (isAppOwner || isSuperUser || isTeamOwner) && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/notifications/*",
      element: NotificationPage,
      title: "Notifications",
      icon: <Alert24Regular fontSize={iconSize} />,
      tabs: ['notifications'],
      show: true && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/my-performance-agreement/*",
      element: MyPerformanceAgreement,
      title: "My Performance Agreement",
      icon: <Handshake24Regular fontSize={iconSize} />,
      tabs: (isTeamOwner || hasSupervisees) ?
        ['My Performance Agreements', 'My Team Agreements Statuses', 'Manage Performance Agreement'] :
        (isAppOwner || isSuperUser ?
        ['My Performance Agreements', 'Manage Performance Agreement'] :
          ['My Performance Agreements']
        ),
      show: true && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/my-performance-assessment/*",
      element: MyPerformanceAssessment,
      title: "My Performance Assessment",
      icon: <ClipboardCheckmark24Regular fontSize={iconSize} />,
      tabs: (isTeamOwner || hasSupervisees) ?
        (isAppOwner || isSuperUser ?
          ['My Assessments', 'My Performances', 'Team Performances', 'My Team Assessments Statuses', 'Manage Performance Assessment'] :
          ['My Assessments', 'My Performances', 'Team Performances', 'My Team Assessments Statuses']
        ) :
        (isAppOwner || isSuperUser ?
          ['My Assessments', 'My Performances', 'Manage Performance Assessment'] :
          ['My Assessments', 'My Performances']
        ),
      show: true && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/employee-dev-plan/*",
      element: EmployeeDevPlan,
      title: "Employee Development Plan",
      icon: <LearningApp24Regular fontSize={iconSize} />,
      tabs: (isSuperUser || isAppOwner) ?
        (isDevMember ?
          ['My Training Dashboard', 'Employees Training', 'Enable Employees Development', 'Annual Organization Development Plans', 'Training & Courses Management', 'Organization Development Team'] :
          ['My Training Dashboard', 'Organization Development Team']) :
        (isDevMember ?
          ['My Training Dashboard', 'Employees Training', 'Enable Employees Development', 'Annual Organization Development Plans', 'Training & Courses Management'] :
          ['My Training Dashboard']),
      show: true && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/performance-calibration/*",
      element: PerformanceCalibration,
      title: "Performance Calibration",
      icon: <Settings24Regular fontSize={iconSize} />,
      tabs: (isSuperUser || isAppOwner) && isPerformanceCalibrationMember ?
        ['Performance Calibration Team', 'Performance Agreements', 'Performance Assessments'] :
        (isPerformanceCalibrationMember) ?
          ['Performance Agreements', 'Performance Assessments'] :
          ['Performance Calibration Team'],
      show: (isAppOwner || isSuperUser || isPerformanceCalibrationMember) && isPerformanceCalibrationModuleEnabled && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/feedback/*",
      element: Feedback,
      title: "Employee 360 Degree Feedback",
      icon: <PersonFeedback24Regular fontSize={iconSize} />,
      tabs: ['feedback'],
      show: (isAppOwner || isSuperUser) && isFeedbackModuleEnabled && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/organization-performance/*",
      element: OrganizationPerformance,
      title: "Organization Performance",
      icon: <DataTrending24Regular fontSize={iconSize} />,
      tabs: ['Organization Performance Assessment', 'Annual Organization Performance'],
      show: (isAppOwner || isSuperUser) && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/annual-corporate-scorecard/*",
      element: AnnualCorporateScorecard,
      title: "Annual Corporate Scorecard",
      icon: <Globe24Regular fontSize={iconSize} />,
      tabs: [
        'Quarterly Corporate Scorecards',
        'Annual Corporate Scorecards',
        ...(isEmployeeCompetencyModuleEnabled ? ['Employee Competency Assessment'] : [])
      ],
      show: (isAppOwner || isSuperUser) && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/reports/*",
      element: Reports,
      title: "Reports",
      icon: <DocumentText24Regular fontSize={iconSize} />,
      tabs: [],
      show: true && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/audit-trail/*",
      element: AuditTrail,
      title: "Audit Trail",
      icon: <Clock24Regular fontSize={iconSize} />,
      tabs: [],
      show: (isAppOwner || isSuperUser) && (isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled)
    },
    {
      path: "/manage/*",
      element: ManagePage,
      title: "Manage Companies",
      icon: <GridRegular fontSize={iconSize} />,
      tabs: ['Companies', 'Performance Super Users', 'Risk Super Users', 'New Risk Super Users', 'Old Strategic Performance Super Users', 'Strategic Performance Super Users', 'Compliance Super Users', 'Internal Audit Super Users', 'Companies Licenses', 'Modules'],
      show: isAppOwner
    },
    // {
    //   path: "/compliance-management/*",
    //   element: ComplianceManagement,
    //   title: "Compliance Management",
    //   icon: <ShieldCheckmark24Regular fontSize={iconSize} />,
    //   tabs: isComplianceSuperUser ?
    //     ['Compliance Reporting', 'Compliance Reviews', 'Quarterly Compliance Updates', 'Compliance Review Cycles', 'Compliance Obligations', 'Compliance Areas'] :
    //     ['Compliance Reporting', 'Quarterly Compliance Updates'],
    //   show: (isComplianceSuperUser || isComplianceChampion) && isComplianceModuleEnabled
    // },
    {
      path: "/new-compliance-management/",
      element: null,
      title: "Compliance Management",
      icon: <ShieldCheckmark24Regular fontSize={iconSize} />,
      tabs: isComplianceSuperUser ?
        ['Compliance Reporting', 'Compliance Reviews', 'Quarterly Compliance Updates', 'Compliance Review Cycles', 'Compliance Obligations', 'Compliance Areas'] :
        ['Compliance Reporting', 'Quarterly Compliance Updates'],
      show: (isComplianceSuperUser || isComplianceChampion) && isComplianceModuleEnabled
    },
    {
      path: "/risk-management/*",
      element: RiskManagement,
      title: "Old Risk Management",
      icon: <ShieldAdd24Regular fontSize={iconSize} />,
      tabs: isRiskSuperUser ?
        ['Dashboard & Reports', 'Residual Risk Assessment', 'My Risk Treatments', 'Risk Treatment Register', 'Risk Assessment', 'Risk Identification', 'Risk Settings'] :
        ['My Risk Treatments'],
      show: (isRiskSuperUser || isRiskChampion) && isRiskModuleEnabled
    },
    {
      path: "/new-risk-management/",
      element: null, // Handled by custom route
      title: "Risk Management",
      icon: <ShieldAdd24Regular fontSize={iconSize} />,
      tabs: isNewRiskManagementSuperUser ?
        ['Dashboard', 'Risk Register', 'Reports', 'Risk Assessment', 'Risk Identification', 'Risk Settings'] :
        ['Dashboard', 'Risk Register'],
      show: (() => {
        const hasRole = isNewRiskManagementSuperUser || isDepartmentUser;
        const isEnabled = isNewRiskManagementModuleEnabled;
        const shouldShow = hasRole && isEnabled;
        return shouldShow;
      })()
    },
    {
      path: "/internal-audit-module",
      element: null, // Handled by custom route
      title: "Internal Audit",
      icon: <ShieldTask24Regular fontSize={iconSize} />,
      tabs: (() => {
        // Super users see all tabs (admin functions)
        if (isInternalAuditModuleSuperUser || isAppOwner) {
          return [
            'Dashboard',
            'Departments',
            'Users',
            'My Tasks',
            'Audit Plans',
            'Audits',
            'Audit Reports',
            'Findings',
            'Action Plans',
            'Audit Configuration',
            'Audit Universe'
          ];
        }
        // Regular users see limited tabs (their tasks and assigned audits)
        return [
          'Dashboard',
          'My Tasks',
          'Audits',
          'Audit Reports',
          'Findings',
          'Action Plans'
        ];
      })(),
      show: (() => {
        // Show to Internal Audit Module Super Users, Internal Audit Users, and App Owners
        const hasRole = isInternalAuditModuleSuperUser || isInternalAuditUser;
        const isEnabled = isInternalAuditModuleEnabled;
        const shouldShow = hasRole && isEnabled;
        console.log('Internal Audit Module Status:', shouldShow);
        console.log('Internal Audit Module Enabled:', isEnabled);
        console.log('Internal Audit Module Super User:', isInternalAuditModuleSuperUser);
        console.log('Internal Audit User:', isInternalAuditUser);
        console.log('Is App Owner:', isAppOwner);
        console.log('Has Role:', hasRole);
        return shouldShow;
      })()
    },
    {
      path: "/old-strategic-performance/",
      element: null, // Handled by custom route
      title: "Old Strategic Performance",
      icon: <Globe24Regular fontSize={iconSize} />,
      tabs: [
        'Employees', 'Departments', 'Strategic Plans', 'Annual Corporate Scorecards', 
        'Employee Scorecards', 'Organization Performance Assessment', 'Reports', 'Approvals'
      ],
      show: (() => {
        // OLD module uses isOldStrategicPerformanceSuperUser (isStrategicPerformanceManagementSuperUser)
        // Note: Old module may have its own employee check - need to verify
        const hasRole = isOldStrategicPerformanceSuperUser; // Only super user for old module
        const isEnabled = isOldStrategicPerformanceModuleEnabled;
        const shouldShow = hasRole && isEnabled;
        console.log('Old Strategic Performance Module Status:', shouldShow);
        console.log('Old Strategic Performance Module Enabled:', isEnabled);
        console.log('Old Strategic Performance Module Super User:', isOldStrategicPerformanceSuperUser);
        return shouldShow;
      })()
    },
    {
      path: "/strategic-performance/",
      element: null, // Handled by custom route
      title: "Strategic Performance",
      icon: <Globe24Regular fontSize={iconSize} />,
      tabs: [],
      show: (() => {
        const hasRole = isStrategicPerformanceSuperUser || isStrategicPerformanceEmployee;
        const isEnabled = isStrategicPerformanceModuleEnabled;
        const shouldShow = hasRole && isEnabled;
        console.log('Strategic Performance Module Status:', shouldShow);
        console.log('Strategic Performance Module Enabled:', isEnabled);
        console.log('Strategic Performance Module Super User:', isStrategicPerformanceSuperUser);
        console.log('Strategic Performance Module Employee:', isStrategicPerformanceEmployee);
        return shouldShow;
      })()
    },
    {
      path: "/teams/*",
      element: TeamsPage,
      title: "Teams",
      icon: <PeopleTeam24Regular fontSize={iconSize} />,
      tabs: (() => {
        console.log('Teams Page Tab Logic Debug:', {
          isComplianceModuleEnabled,
          isRiskModuleEnabled,
          isNewRiskManagementModuleEnabled,
          isPerformanceManagementModuleEnabled,
          isPerformanceManagementQuantitativeModuleEnabled,
          isPerformanceManagementCascadeModuleEnabled,
          isSuperUser,
          isAppOwner
        });
        
        // Check if user is super user and performance management is enabled
        const isPerformanceManagementEnabled = isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled;
        const shouldShowTeamsTab = (isSuperUser || isAppOwner) && isPerformanceManagementEnabled;
        
        // Build tabs array without 'Teams' first
        const baseTabsWithoutTeams = isComplianceModuleEnabled
        ? isRiskModuleEnabled
            ? isNewRiskManagementModuleEnabled
              ? isPerformanceManagementEnabled
              ? ['Performance User', 'Compliance User', 'Risk User', 'New Risk User']
              : ['Compliance User', 'Risk User', 'New Risk User']
              : isPerformanceManagementEnabled
              ? ['Performance User', 'Compliance User', 'Risk User']
              : ['Compliance User', 'Risk User']
            : isNewRiskManagementModuleEnabled
              ? isPerformanceManagementEnabled
              ? ['Performance User', 'Compliance User', 'New Risk User']
              : ['Compliance User', 'New Risk User']
              : isPerformanceManagementEnabled
              ? ['Performance User', 'Compliance User']
              : ['Compliance User']
        : isRiskModuleEnabled
          ? isNewRiskManagementModuleEnabled
            ? isPerformanceManagementEnabled
            ? ['Performance User', 'Risk User', 'New Risk User']
            : ['Risk User', 'New Risk User']
            : isPerformanceManagementEnabled
            ? ['Performance User', 'Risk User']
            : ['Risk User']
          : isNewRiskManagementModuleEnabled
            ? isPerformanceManagementEnabled
            ? ['Performance User', 'New Risk User']
            : ['New Risk User']
            : isPerformanceManagementEnabled
            ? ['Performance User']
            : [];

        // Conditionally add 'Strategic Performance User' if module is enabled
        if (isStrategicPerformanceModuleEnabled) {
          baseTabsWithoutTeams.push('Strategic Performance User');
        }

        // Conditionally add 'Teams' tab at the beginning if conditions are met
        const baseTabs = shouldShowTeamsTab 
          ? ['Teams', ...baseTabsWithoutTeams]
          : baseTabsWithoutTeams;

        // Conditionally add 'Internal Audit Module User' if module is enabled
        if (isInternalAuditModuleEnabled) {
          baseTabs.push('Internal Audit Module User');
        }

        return baseTabs;
      })(),
      show: (() => {
        // For internal audit module users, only show Teams to super users (not auditors or department users)
        if (isInternalAuditUser && !isInternalAuditModuleSuperUser) {
          return false;
        }
        // Show Teams to all other super users and app owners
        return isAppOwner || isSuperUser || isComplianceSuperUser || isRiskSuperUser || isNewRiskManagementSuperUser || isStrategicPerformanceSuperUser || isInternalAuditModuleSuperUser;
      })()
    }
  ];

  if (!modulesChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    <Routes>
      {/* New Risk Management with custom layout */}
      <Route path="/new-risk-management/*" element={
        <NewRiskManagementLayout pages={pages}>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }>
            <NewRiskManagement />
          </Suspense>
        </NewRiskManagementLayout>
      } />
      
      {/* New Compliance Management with custom layout */}
      <Route path="/new-compliance-management/*" element={
        <NewComplianceManagementLayout pages={pages}>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }>
            <NewComplianceManagement />
          </Suspense>
        </NewComplianceManagementLayout>
      } />
      
      {/* Strategic Performance with custom layout */}
      <Route path="/old-strategic-performance/*" element={
        <OldStrategicPerformanceLayout pages={pages}>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }>
            <OldStrategicPerformance />
          </Suspense>
        </OldStrategicPerformanceLayout>
      } />
      
      {/* New Strategic Performance with custom layout */}
      <Route path="/strategic-performance" element={<Navigate to="/strategic-performance/approvals" replace />} />
      <Route path="/strategic-performance/*" element={
        <StrategicPerformanceLayout pages={pages}>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }>
            <StrategicPerformance />
          </Suspense>
        </StrategicPerformanceLayout>
      } />
      
      {/* Internal Audit Module with custom layout */}
      <Route path="/internal-audit-module" element={<Navigate to="/internal-audit-module/index" replace />} />
      <Route path="/internal-audit-module/*" element={
        <InternalAuditModuleLayout pages={pages}>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          }>
            <InternalAuditModule />
          </Suspense>
        </InternalAuditModuleLayout>
      } />
      
      <Route element={<Layout pages={pages} />}>
        <Route path="/*" element={
          isAppOwner ? (
            <Navigate to="/manage" replace />
          ) : (
            (
              !isPerformanceManagementModuleEnabled &&
              !isPerformanceManagementQuantitativeModuleEnabled &&
              !isPerformanceManagementCascadeModuleEnabled &&
              !isRiskModuleEnabled &&
              !isNewRiskManagementModuleEnabled &&
              !isComplianceModuleEnabled &&
              !isOldStrategicPerformanceModuleEnabled &&
              !isStrategicPerformanceModuleEnabled &&
              !isInternalAuditModuleEnabled
            ) ? (
              <Navigate to="/no-module-enabled" replace />
            ) : isPerformanceManagementModuleEnabled || isPerformanceManagementQuantitativeModuleEnabled || isPerformanceManagementCascadeModuleEnabled ? (
              (isSuperUser || isTeamOwner) ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/notifications" replace />
              )
             ) : isOldStrategicPerformanceModuleEnabled && isOldStrategicPerformanceSuperUser ? (
               <Navigate to="/old-strategic-performance" replace />
            ) : isStrategicPerformanceModuleEnabled && (isStrategicPerformanceSuperUser || isStrategicPerformanceEmployee) ? (
              <Navigate to="/strategic-performance" replace />
            ) : isNewRiskManagementModuleEnabled && (isNewRiskManagementSuperUser || isDepartmentUser) ? (
              <Navigate to="/new-risk-management" replace />
            ) : isComplianceModuleEnabled && (isComplianceSuperUser || isComplianceChampion) ? (
              <Navigate to="/new-compliance-management" replace />
            ) : isInternalAuditModuleEnabled && (isInternalAuditModuleSuperUser || isInternalAuditUser) ? (
              <Navigate to="/internal-audit-module" replace />
            ) : isRiskModuleEnabled && (isRiskSuperUser || isRiskChampion) ? (
              <Navigate to="/risk-management" replace />
            ) : (isRiskModuleEnabled || isNewRiskManagementModuleEnabled || isComplianceModuleEnabled || isOldStrategicPerformanceModuleEnabled || isStrategicPerformanceModuleEnabled || isInternalAuditModuleEnabled) ? (
              <Navigate to="/no-permission" replace />
            ) : (
              <Navigate to="/no-module-enabled" replace />
            )
          )
        } />
        {pages.map((page) => (
          page.show && page.element && (
            <Route
              key={page.path}
              path={page.path}
              element={
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                  </Box>
                }>
                  <page.element
                    title={page.title}
                    icon={page.icon}
                    tabs={page.tabs}
                    path={page.path}
                    show={page.show}
                    element={page.element}
                  />
                </Suspense>
              }
            />
          )
        ))}
      </Route>

      {/* Support Dashboard (Admin) */}
      <Route path="/support-dashboard" element={<SupportDashboard />} />
    </Routes>

    {/* Global Support Chat Widget */}
    <SupportChatWidget />
    </>
  );
}

export default Main; 
